import { useEffect, useId, useRef } from 'react';

import {
  Box,
  LinearProgress,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { LinkProps } from '@tanstack/react-router';
import { Row, Table as TanstackTable } from '@tanstack/react-table';

import TableBodyRow from './TableBodyRow';
import TableFooter from './TableFooter';
import TableHeaderRow from './TableHeaderRow';
import TableOverlay from './TableOverlay';

interface TableProps<T> {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  table: TanstackTable<T>;
  tableLabel: string;
  errorText: string;
  emptyHeading?: string;
  emptyText: string;
  emptyIcon?: React.ReactNode;
  emptyActions?: React.ReactNode;
  showCircularProgressOnly?: boolean;
  ctaText?: string;
  ctaLink?: LinkProps['to'];
  onRowClick?: (row: Row<T>) => void;
}

const Table = <T,>({
  isLoading,
  isFetching,
  isError,
  table,
  tableLabel,
  errorText,
  emptyHeading = 'No results found',
  emptyText,
  emptyIcon,
  emptyActions,
  showCircularProgressOnly = false,
  ctaText,
  ctaLink,
  onRowClick,
}: TableProps<T>) => {
  const id = useId();
  const pageSizeSelectId = `rows-per-page-select-${id}`;

  const columns = table.getAllColumns();
  const pagination = table.getState().pagination;
  const data = table.options.data;
  const hasData = data.length > 0;
  const isPaginated = pagination.pageIndex >= 0;

  const showOverlay =
    isLoading ||
    (isFetching ? !hasData : !hasData || isError) ||
    (showCircularProgressOnly && isFetching);

  const paginationRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLTableSectionElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const leftLeafColumns = table.getLeftLeafColumns();
  const rightLeafColumns = table.getRightLeafColumns();
  const lastLeftColId = leftLeafColumns[leftLeafColumns.length - 1]?.id;
  const firstRightColId = rightLeafColumns[0]?.id;

  // Drive live column-resize feedback via CSS custom properties + rAF so that
  // body rows are never re-rendered on every mousemove. Cells read
  // `var(--col-live-size-{id}, committedSize)` — the variable is set on the
  // container while dragging and removed on mouseup so the committed size takes
  // over automatically.
  const { isResizingColumn, startOffset, startSize } =
    table.getState().columnSizingInfo;
  const totalSize = table.getTotalSize();

  useEffect(() => {
    if (!isResizingColumn || startOffset === null || startSize === null) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    let latestClientX = startOffset;
    let rafId: number | null = null;

    const onMove = (e: MouseEvent) => {
      latestClientX = e.clientX;
      if (rafId !== null) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const delta = latestClientX - startOffset;
        container.style.setProperty(
          `--col-live-size-${isResizingColumn}`,
          `${String(Math.max(startSize + delta, 0))}px`
        );
        container.style.setProperty(
          '--table-total-size',
          `${String(totalSize + delta)}px`
        );
      });
    };

    document.addEventListener('mousemove', onMove);
    return () => {
      document.removeEventListener('mousemove', onMove);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      container.style.removeProperty(`--col-live-size-${isResizingColumn}`);
      // --table-total-size is managed by the style prop; no cleanup needed here.
    };
  }, [isResizingColumn, startOffset, startSize, totalSize]);

  const updateScrollState = () => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    el.style.setProperty(
      '--shadow-left',
      el.scrollLeft > 0 ? '6px 0 12px 2px rgba(0,0,0,0.18)' : 'none'
    );
    el.style.setProperty(
      '--shadow-right',
      el.scrollLeft < el.scrollWidth - el.clientWidth - 1
        ? '-6px 0 12px 2px rgba(0,0,0,0.18)'
        : 'none'
    );
  };

  useEffect(() => {
    updateScrollState();
  }, []);

  return (
    <Box position='relative'>
      <Box position='relative'>
        <TableContainer
          ref={containerRef}
          onScroll={updateScrollState}
          style={
            {
              '--table-total-size': `${String(totalSize)}px`,
            } as React.CSSProperties
          }
          sx={{ minHeight: showOverlay ? 400 : 'auto' }}
        >
          {showOverlay ? (
            <TableOverlay
              isLoading={isLoading}
              isFetching={isFetching}
              hasData={hasData}
              showCircularProgressOnly={showCircularProgressOnly}
              isError={isError}
              emptyIcon={emptyIcon}
              emptyHeading={emptyHeading}
              emptyText={emptyText}
              errorText={errorText}
              ctaText={ctaText}
              ctaLink={ctaLink}
              emptyActions={emptyActions}
              isPaginated={isPaginated}
              headerHeight={headerRef.current?.clientHeight ?? 0}
              paginationHeight={paginationRef.current?.clientHeight ?? 0}
            />
          ) : null}

          <MuiTable
            aria-label={tableLabel}
            size='small'
            sx={{
              tableLayout: 'fixed',
              minWidth: 'var(--table-total-size)',
            }}
          >
            <TableHead ref={headerRef}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableHeaderRow
                  key={headerGroup.id}
                  headerGroup={headerGroup}
                  isRowSelectionEnabled={!!table.options.enableRowSelection}
                  lastLeftColId={lastLeftColId}
                  firstRightColId={firstRightColId}
                />
              ))}

              {isFetching && hasData && !showCircularProgressOnly ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    padding='none'
                    sx={{ border: 0, height: 2 }}
                  >
                    <LinearProgress sx={{ height: 2 }} />
                  </TableCell>
                </TableRow>
              ) : null}
            </TableHead>

            {!showOverlay ? (
              <TableBody sx={{ position: 'relative' }}>
                {table.getRowModel().rows.map((row, index) => (
                  <TableBodyRow
                    key={row.id}
                    row={row}
                    isLast={index === table.getRowModel().rows.length - 1}
                    lastLeftColId={lastLeftColId}
                    firstRightColId={firstRightColId}
                    onRowClick={onRowClick}
                  />
                ))}
              </TableBody>
            ) : null}
          </MuiTable>
        </TableContainer>
      </Box>

      {isPaginated ? (
        <TableFooter
          table={table}
          isFetching={isFetching}
          isError={isError}
          hasData={hasData}
          paginationRef={paginationRef}
          pageSizeSelectId={pageSizeSelectId}
        />
      ) : null}
    </Box>
  );
};

export default Table;
